import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { waitForToken } from '@app/redux/utils';
import { saveAlertNameInStorage } from '@app/utils';

import { BillingAccount, Etf, PurchaseRetentionOfferInput, RetentionOffer } from '@mfe/shared/schema-types';

import { Alert, parseAccountAlerts, removeAlert } from '../alerts';
import { selectHasFiber, selectLocation, selectUser } from '../auth';
import { getBillingAccount, selectBillingAccount } from '../billingInfo';
import { selectConfig } from '../config';
import {
  ErrorType,
  FetchWithErrorsMutation,
  FetchWithErrorsQuery,
  graphqlMutationWithErrors,
  graphqlQueryWithErrors,
} from '../graphql';
import { ScrubAddressModalType, setShowScrubModal } from '../scrubbedAddress';
import {
  CANCEL_ORDER,
  DELETE_VIASAT_INTERNET,
  GET_ETF,
  GET_RETENTION_OFFERS,
  GET_SCHEDULED_FOR_DELETION_ORDERS,
  PURCHASE_RETENTION_OFFER,
  SEND_SURVEY_TO_SALESFORCE,
} from './requests';
import {
  disconnectViasatInternet,
  getEtf,
  getPurchaseRetentionOffers,
  getRetentionOffers,
  ProcessScrubAction,
  processScrubResult,
  resetDisconnectInternet,
  selectSurveyValues,
  sendSurveyResultsToSalesforce,
  setCancelInFlightOrder,
  setCancelOrder,
  setDisconnectCaseInfo,
  setDisconnectError,
  setEtf,
  setEtfError,
  setHasCancelledDisconnectOrder,
  setHasCancelledInFlightOrder,
  setIsResumeServiceLoading,
  setPurchaseRetentionOfferError,
  setPurchaseRetentionOfferSuccess,
  setRetentionOffers,
  setRetentionOffersError,
  setScheduledToDisconnectGlobalAlert,
  setViasatInternetDisconnected,
} from './slice';
import { sanitizeAddressLines } from '../disconnect/utils';
import {
  ScrubAddressProcessStatus,
  ScrubAddressRecommendation,
} from '../../../../platform/src/server/dataSources/PAL/types';
import { ProductInstanceTypes } from '../../../../platform/src/server/dataSources/PSM/types';

export function* sendFormToSalesforce(action: PayloadAction<boolean>) {
  const { disconnectReason, otherReason, provider, salesforceCode } = yield select(selectSurveyValues);

  const salesforceInput = {
    disconnectReason: otherReason ? otherReason : disconnectReason,
    provider,
    isCif: action.payload,
    salesforceCode,
  };

  try {
    const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
      mutation: SEND_SURVEY_TO_SALESFORCE,
      variables: { input: salesforceInput },
      fetchPolicy: 'no-cache',
    });
    const { errors, runtimeError } = response;

    if (errors || runtimeError) {
      console.error(`Error sending form to Salesforce: ${runtimeError ?? errors}`);
      return;
    }
    const caseNumber = response?.data?.sendSurveyToSalesforce?.status;

    if (!caseNumber) {
      console.warn('CasecaseNumber not returned from sendSurveyToSalesforce');
      return;
    }

    yield put(setDisconnectCaseInfo(caseNumber));
  } catch (error) {
    console.error(`Error in sendFormToSalesforce saga: ${error}`);
  }
}

export function* fetchRetentionOffers() {
  yield call(waitForToken);

  const response: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_RETENTION_OFFERS,
  });

  const { data, errors, runtimeError } = response;

  if (errors || runtimeError) {
    yield put(setRetentionOffersError(runtimeError ?? errors));
    return;
  }

  if (data?.getRetentionOffers) {
    yield put(setRetentionOffers(data?.getRetentionOffers as RetentionOffer[]));
  }
}

export function* displayScheduledToDisconnectGlobalAlert() {
  const billingAccount: BillingAccount = yield select(selectBillingAccount);
  const { billDate } = billingAccount;

  if (!billDate) {
    yield put(getBillingAccount());
  }

  try {
    const response: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
      query: GET_SCHEDULED_FOR_DELETION_ORDERS,
    });

    const { data } = response;

    if (data?.getScheduledForDeletionOrders?.orderId && billDate) {
      yield put(setViasatInternetDisconnected());
    }
  } catch (error) {
    console.error(error);
  }
}

export function* purchaseRetentionOffer(action: PayloadAction<PurchaseRetentionOfferInput>) {
  try {
    const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
      mutation: PURCHASE_RETENTION_OFFER,
      variables: { input: action.payload },
      fetchPolicy: 'no-cache',
    });
    const { data, errors, runtimeError } = response;

    if (errors || runtimeError) {
      yield put(setPurchaseRetentionOfferError(runtimeError ?? errors));
      return;
    }

    if (data?.purchaseRetentionOffer?.orderId) {
      yield put(setPurchaseRetentionOfferSuccess(true));
    }
  } catch (error) {
    yield put(setPurchaseRetentionOfferError(error));
  }
}

export function* deleteViasatInternet(action: PayloadAction<string>) {
  const { coordinates, address } = yield select(selectLocation);
  const { sendSurveyToSalesforce } = yield select(selectConfig);
  const { salesforceCode } = yield select(selectSurveyValues);

  try {
    const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
      mutation: DELETE_VIASAT_INTERNET,
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          ...sanitizeAddressLines(address, coordinates),
          deactivateOn: action.payload,
          notes: salesforceCode,
        },
      },
    });

    const { data, errors, runtimeError } = response;

    if (errors || runtimeError) {
      yield put(setDisconnectError(runtimeError ?? errors));
      return;
    }

    if (data?.deleteViasatInternet?.status?.toLowerCase() === 'success') {
      yield put(setViasatInternetDisconnected());
      if (sendSurveyToSalesforce) {
        yield put(sendSurveyResultsToSalesforce(false));
      }
    }
  } catch (error) {
    yield put(setDisconnectError(error as ErrorType));
  }
}

export function* cancelInFlightOrder() {
  const { sendSurveyToSalesforce } = yield select(selectConfig);
  const { salesforceCode } = yield select(selectSurveyValues);

  try {
    const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
      mutation: CANCEL_ORDER,
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          reason: salesforceCode ? salesforceCode : 'Retention',
          cif: true,
        },
      },
    });

    const { data, errors, runtimeError } = response;

    if (errors || runtimeError) {
      yield put(setDisconnectError(runtimeError ?? errors));
      return;
    }

    if (data?.cancelOrder) {
      if (sendSurveyToSalesforce) {
        //set action payload to true to signify that this is a CIF order
        yield put(sendSurveyResultsToSalesforce(true));
      }
      const alerts: Alert[] = yield call(parseAccountAlerts);
      for (const alert of alerts) {
        saveAlertNameInStorage(alert.type);
        yield put(removeAlert(alert.type));
      }
      yield put(setHasCancelledInFlightOrder(true));

      sessionStorageManager.setItem(SESSION_STORAGE_VARIABLES.CIF_ORDER_SUCCESS, true);
      yield put(setViasatInternetDisconnected());
    }
  } catch (error) {
    yield put(setDisconnectError(error as ErrorType));
  }
}

export function* cancelOrder() {
  yield put(setIsResumeServiceLoading(true));

  try {
    const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
      mutation: CANCEL_ORDER,
      fetchPolicy: 'no-cache',
      variables: {
        input: { reason: 'Retention' },
      },
    });

    const { data, errors, runtimeError } = response;
    yield put(setIsResumeServiceLoading(false));

    if (errors || runtimeError) {
      yield put(setViasatInternetDisconnected());
    }

    if (data?.cancelOrder) {
      yield put(resetDisconnectInternet());
      yield put(setHasCancelledDisconnectOrder(true));
    }
  } catch (error) {
    console.error(`Error cancelling order: ${error}`);
    yield put(setViasatInternetDisconnected());
  }
}

export function* fetchEarlyTerminationFees() {
  yield call(waitForToken);

  const {
    user: { productKind },
  } = yield select(selectUser);

  const hasWirelessInternet = productKind === ProductInstanceTypes.WirelessInternet;

  const hasFiber: boolean = yield select(selectHasFiber);

  if (hasWirelessInternet || hasFiber) {
    return;
  }

  const response: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_ETF,
  });

  const { data, errors, runtimeError } = response;

  if (errors || runtimeError) {
    yield put(setEtfError(runtimeError ?? errors));
    return;
  }

  if (data?.getEarlyTerminationFees) {
    yield put(setEtf(data.getEarlyTerminationFees as Etf[]));
  }
}

export function* processScrubbedAddress(action: ProcessScrubAction) {
  const { processStatus, recommendation } = action.payload.scrubbedAddress;

  // Copy-paste from addOnsSagas
  // To do: use the same implementation for both disconnect & voice
  if (
    processStatus === ScrubAddressProcessStatus.Verified ||
    (processStatus === ScrubAddressProcessStatus.Corrected && recommendation === ScrubAddressRecommendation.RECOMMEND)
  ) {
    yield put(setShowScrubModal(null));
    return;
  }

  if (processStatus === ScrubAddressProcessStatus.Incorrect && recommendation === ScrubAddressRecommendation.REJECT) {
    yield put(setShowScrubModal(ScrubAddressModalType.IncorrectAddress));
    return;
  }

  if (processStatus === ScrubAddressProcessStatus.Corrected) {
    return;
  }
  yield put(setShowScrubModal(ScrubAddressModalType.AddressError));
}

export function* watchDisconnect() {
  yield takeLatest(getEtf.type, fetchEarlyTerminationFees);
  yield takeLatest(processScrubResult.type, processScrubbedAddress);
  yield takeLatest(getRetentionOffers.type, fetchRetentionOffers);
  yield takeLatest(getPurchaseRetentionOffers.type, purchaseRetentionOffer);
  yield takeLatest(disconnectViasatInternet.type, deleteViasatInternet);
  yield takeLatest(setCancelOrder.type, cancelOrder);
  yield takeLatest(setScheduledToDisconnectGlobalAlert.type, displayScheduledToDisconnectGlobalAlert);
  yield takeLatest(setCancelInFlightOrder.type, cancelInFlightOrder);
  yield takeLatest(sendSurveyResultsToSalesforce.type, sendFormToSalesforce);
}
